import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Input } from '../../Input/Input.es.js';
import { SelectList } from '../../SelectList/SelectList.es.js';
import { removeTimeFromDate } from '../DatePicker.PresetRange/DatePicker.PresetRange.es.js';
import { DatePickerSingle } from '../DatePicker.Single/DatePicker.Single.es.js';
import { DatePickerInner } from '../DateRange.Inner/DatePicker.Inner.es.js';

const DatePickerPreset = /*#__PURE__*/ forwardRef(({ onChange, presetOptions, locale = {
    oneWeekFromNow: '1 week from now',
    twoWeeksFromNow: '2 weeks from now',
    oneMonthFromNow: '1 month from now',
    sixMonthsFromNow: '6 months from now',
    twelveMonthsFromNow: '12 months from now',
    custom: 'Custom date'
}, ...props }, ref)=>{
    const [selectedPreset, setSelectedPreset] = useState();
    const [selectedDate, setSelectedDate] = useState();
    const [popper, setPopper] = useState(false);
    const presetRef = useRef(null);
    const clearButtonRef = useRef(null);
    const fieldRef = useRef(null);
    const selectListPresets = useMemo(()=>{
        // if presetOptions have been supplied, return those as there is now reason to generate default presets & locales
        if (presetOptions) return presetOptions;
        const today = new Date();
        return [
            {
                value: removeTimeFromDate(new Date(new Date().setDate(today.getDate() + 7))),
                label: locale.oneWeekFromNow
            },
            {
                value: removeTimeFromDate(new Date(new Date().setDate(today.getDate() + 14))),
                label: locale.twoWeeksFromNow
            },
            {
                value: removeTimeFromDate(new Date(new Date().setMonth(today.getMonth() + 1))),
                label: locale.oneMonthFromNow
            },
            {
                value: removeTimeFromDate(new Date(new Date().setMonth(today.getMonth() + 6))),
                label: locale.sixMonthsFromNow
            },
            {
                value: removeTimeFromDate(new Date(new Date().setFullYear(today.getFullYear() + 1))),
                label: locale.twelveMonthsFromNow
            },
            {
                value: 'custom',
                label: locale.custom
            }
        ];
    }, [
        locale,
        presetOptions
    ]);
    const handlePresetChange = useCallback((preset)=>{
        if (preset) {
            setSelectedDate(null);
            setSelectedPreset(preset);
            if (preset.value !== undefined && preset?.value !== 'custom') {
                setSelectedDate(preset.value);
                onChange?.(removeTimeFromDate(preset.value), undefined);
            }
        }
    }, [
        onChange
    ]);
    const handleCustomSingleChange = useCallback((date, event)=>{
        setSelectedDate(date);
        if (date) {
            onChange?.(removeTimeFromDate(date), event);
        }
    }, [
        onChange
    ]);
    const handleClearDate = useCallback((event)=>{
        event.stopPropagation();
        onChange?.(null, event);
        setSelectedPreset(undefined);
        setSelectedDate(undefined);
    }, [
        onChange
    ]);
    const handleInputClick = useCallback(()=>{
        setPopper(true);
    }, []);
    const handleOutsideClick = useCallback((e)=>{
        if (presetRef.current && !presetRef.current.contains(e.target)) {
            setPopper(false);
        }
    }, []);
    useEffect(()=>{
        document.addEventListener('mousedown', handleOutsideClick);
        return ()=>{
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [
        handleOutsideClick
    ]);
    return jsxs("div", {
        ref: presetRef,
        className: "cb-date-picker-preset",
        children: [
            jsx(DatePickerInner, {
                ...props,
                ref: ref,
                clearButtonRef: clearButtonRef,
                fieldRef: fieldRef,
                handleClearDate: handleClearDate,
                isClearable: selectedPreset !== undefined,
                locale: locale,
                onClick: handleInputClick,
                value: selectedPreset ? selectedPreset.label : ''
            }),
            popper && jsxs("div", {
                className: "preset-popper-container",
                "data-testid": "preset-popper",
                children: [
                    jsx("div", {
                        className: "preset-options-container",
                        children: jsx(SelectList, {
                            selected: selectedPreset,
                            children: selectListPresets.map((preset)=>jsx(SelectList.Item, {
                                    "data-testid": "list-item",
                                    handleSelect: handlePresetChange,
                                    item: preset
                                }, preset.value?.toString()))
                        })
                    }),
                    selectedPreset?.value === 'custom' && jsxs(Fragment, {
                        children: [
                            jsx("hr", {
                                className: "divider-col"
                            }),
                            jsxs("div", {
                                className: "date-range-container",
                                children: [
                                    jsx("div", {
                                        className: "input-container",
                                        children: jsx(Input, {
                                            className: "date-input",
                                            label: "Date",
                                            children: jsx(Input.Field.Text, {
                                                name: "selected",
                                                type: "input",
                                                value: selectedDate?.toLocaleDateString(locale?.code) ?? '',
                                                readOnly: true
                                            })
                                        })
                                    }),
                                    jsx("hr", {
                                        className: "divider-row"
                                    }),
                                    jsx(DatePickerSingle, {
                                        intent: "filter",
                                        locale: locale,
                                        monthsShown: 1,
                                        onChange: handleCustomSingleChange,
                                        selected: selectedDate,
                                        inline: true,
                                        ...props
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
DatePickerPreset.displayName = 'DatePicker.Preset';

export { DatePickerPreset };
//# sourceMappingURL=DatePicker.Preset.es.js.map
